import Component from '@glimmer/component';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import type ScreenerResult from 'garaje/models/screener-result';
import type { IdScanningNameMatch } from 'garaje/models/screener-result';
import type { GuestWithVisitApproval } from 'garaje/models/visit-approval';

interface IdNameMismatchResolutionSignature {
  Args: {
    model: GuestWithVisitApproval;
  };
}

/**
 * Summarizes the outcome of a reviewed ID name-mismatch (approved or denied) in the
 * entry/invite details.
 */
export default class IdNameMismatchResolution extends Component<IdNameMismatchResolutionSignature> {
  get result(): ScreenerResult | undefined {
    return this.args.model.visitApproval.idScanningResult;
  }

  get nameMatch(): IdScanningNameMatch | undefined {
    const { result } = this;
    if (!result?.isIdScanning()) return undefined;
    return result.report['name-match'];
  }

  get isApproved(): boolean {
    return Boolean(this.result?.isApproved);
  }

  get nameUpdated(): boolean {
    return this.isApproved && this.nameMatch?.['name-action'] === 'updated';
  }

  get nameRemoved(): boolean {
    return this.isApproved && this.nameMatch?.['name-action'] === 'removed';
  }

  /** Reviewer who resolved the id-scanning mismatch (latest id-scanning review). */
  get reviewerName(): string | undefined {
    const reviews = this.args.model.visitApproval.screenerReviews.filter((review) => review.isIdScanning());
    return reviews[reviews.length - 1]?.reviewerName;
  }

  /**
   * The name that was compared against the ID: the profile's stored legal name
   * when one exists (`comparison-left`), otherwise the typed entry/invite name.
   * Mirrors the mismatch email's REGISTERED NAME.
   */
  get registeredName(): string {
    return this.nameMatch?.['comparison-left'] || this.args.model.fullName;
  }

  /** Name read from the government ID. */
  get idName(): string {
    return [this.nameMatch?.['veriff-first-name'], this.nameMatch?.['veriff-last-name']].filter(Boolean).join(' ');
  }

  /** Legal name applied on approve. */
  get appliedName(): string {
    return [this.nameMatch?.['applied-first-name'], this.nameMatch?.['applied-last-name']].filter(Boolean).join(' ');
  }

  <template>
    <div class="mb-8" data-test-id-name-mismatch-resolution>
      <h4 class="text-sm font-bold mb-2">ID verification</h4>
      <div class="text-sm ml-2 text-carbon-70">
        <div class="flex items-center mb-2" data-test-id-name-mismatch-status>
          {{#if this.isApproved}}
            {{svgJar "checkmark-darkgray" height="14" width="14" class="flex-none mr-2"}}
          {{/if}}
          <span>
            Name mismatch
            {{if this.isApproved "approved" "denied"}}
            {{#if this.reviewerName}}by
              <span data-private>{{this.reviewerName}}</span>{{/if}}
          </span>
        </div>

        {{#if this.nameUpdated}}
          <div class="flex items-center mb-2" data-test-id-name-mismatch-updated>
            {{svgJar "checkmark-darkgray" height="14" width="14" class="flex-none mr-2"}}
            <span>Name updated</span>
          </div>
          <div class="ml-2">
            <div><span class="text-carbon-50">Before:</span> <span data-private>{{this.registeredName}}</span></div>
            <div><span class="text-carbon-50">New:</span> <span data-private>{{this.appliedName}}</span></div>
          </div>
        {{else if this.nameRemoved}}
          <div class="flex items-center mb-2" data-test-id-name-mismatch-removed>
            {{svgJar "checkmark-darkgray" height="14" width="14" class="flex-none mr-2"}}
            <span>Name removed</span>
          </div>
        {{else}}
          <div class="ml-2">
            <div><span class="text-carbon-50">Registered:</span>
              <span data-private>{{this.registeredName}}</span></div>
            <div><span class="text-carbon-50">ID:</span> <span data-private>{{this.idName}}</span></div>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
